'use client';

import { Field, Form, Formik, type FormikHelpers } from 'formik';
import { useEffect, type ReactNode } from 'react';
import { object, string } from 'yup';
import {
  InteractionApiError,
  isTerminalInteractionError,
  loginInteraction,
  reportInteractionFailure,
} from '@/app/utils/auth-interactions-api';
import type { InteractionApprovedHandler, InteractionTerminalHandler } from './types';
import {
  FormIntro,
  FormSwitchButton,
  InputFieldset,
  TurnstileField,
  formErrorMessage,
  turnstileSiteKey,
  turnstileTheme,
  useTurnstileReset,
} from './shared-fields';

interface LoginFormValues {
  base: string;
  email: string;
  password: string;
  turnstile: string;
}

// Keep in sync with the legacy login schema in app/utils/auth-api.ts.
const loginValidationSchema = object().shape({
  email: string().email('Invalid email').required('Required'),
  password: string().required('Required'),
  turnstile: string().required('Please complete the CAPTCHA'),
});

export default function InteractionLoginForm({
  interactionToken,
  onApproved,
  onTerminal,
  onBegin,
  onSwitchToSignup,
  onSwitchToMagicLink,
  hideHeader = false,
  allowOutboundLinks = true,
  passwordRecoveryHref,
}: {
  interactionToken: string;
  onApproved: InteractionApprovedHandler;
  onTerminal: InteractionTerminalHandler;
  onBegin: () => void;
  onSwitchToSignup: () => void;
  onSwitchToMagicLink: () => void;
  hideHeader?: boolean;
  allowOutboundLinks?: boolean;
  passwordRecoveryHref?: string;
}) {
  const { turnstileKey, resetTurnstile } = useTurnstileReset();
  let recoveryLink: ReactNode = null;
  if (passwordRecoveryHref) {
    recoveryLink = (
      <a
        href={passwordRecoveryHref}
        className="underline text-slate-400 hover:text-slate-700 font-medium"
      >
        Forgot password
      </a>
    );
  } else if (allowOutboundLinks) {
    recoveryLink = (
      <a
        href="https://penzu.com/app/forgot-password"
        className="underline text-slate-400 hover:text-slate-700 font-medium"
      >
        Forgot password
      </a>
    );
  }

  useEffect(() => {
    onBegin();
  }, [onBegin]);

  function resetSubmittedTurnstile(actions: FormikHelpers<LoginFormValues>) {
    actions.setFieldValue('turnstile', '', false);
    resetTurnstile();
  }

  return (
    <>
      {!hideHeader && (
        <FormIntro title="Log in">
          <p>Enter your email and password to log in to your Penzu account.</p>
          <p>
            Don&apos;t have an account?{' '}
            <FormSwitchButton onClick={onSwitchToSignup}>Sign up here.</FormSwitchButton>
          </p>
        </FormIntro>
      )}

      <Formik
        initialValues={{
          base: '',
          email: '',
          password: '',
          turnstile: '',
        }}
        validationSchema={loginValidationSchema}
        onSubmit={async (values, actions: FormikHelpers<LoginFormValues>) => {
          try {
            const result = await loginInteraction({
              interactionToken,
              email: values.email,
              password: values.password,
              turnstile: values.turnstile,
            });
            const completed = await onApproved(result.resumeUrl);
            if (!completed) {
              actions.setSubmitting(false);
            }
          } catch (error) {
            actions.setSubmitting(false);
            resetSubmittedTurnstile(actions);
            if (error instanceof InteractionApiError) {
              if (isTerminalInteractionError(error)) {
                onTerminal(error);
                return;
              }
              actions.setErrors({ base: formErrorMessage(error) });
              return;
            }
            reportInteractionFailure(error, {
              source: 'interaction-login-form',
              endpoint: '/api/auth/interactions/login',
              operation: 'submit',
            });
            actions.setErrors({
              base: 'Login failed: Please try again.',
            });
          }
        }}
      >
        {({ isSubmitting, isValid, dirty, errors }) => (
          <Form>
            {errors.base && <div className="form-error">{errors.base}</div>}
            <fieldset className="max-w-sm md:max-w-full md:w-lg flex flex-col space-y-4 mb-4">
              <InputFieldset
                name="email"
                label="Email"
                type="email"
                placeholder="eg. you@example.com"
                autoComplete="username"
              />
              <InputFieldset
                name="password"
                label="Password"
                type="password"
                placeholder="Enter your password"
                autoComplete="current-password"
              />
            </fieldset>
            <fieldset className="max-w-sm md:w-lg flex flex-col space-y-4 mb-6">
              <Field
                name="turnstile"
                component={TurnstileField}
                siteKey={turnstileSiteKey}
                theme={turnstileTheme}
                widgetKey={turnstileKey}
                action="login"
                onWidgetError={(error: unknown) =>
                  reportInteractionFailure(error, {
                    source: 'interaction-login-form',
                    endpoint: 'turnstile',
                    operation: 'widget',
                  })
                }
              />
            </fieldset>
            <div className="mb-4">
              <button
                type="submit"
                className="btn text-white bg-blue-500 hover:bg-blue-700 w-full"
                disabled={isSubmitting || !isValid || !dirty}
              >
                Log in
              </button>
            </div>
            <div className="text-sm flex flex-col sm:flex-row sm:justify-between gap-2">
              {recoveryLink}
              <button
                type="button"
                onClick={onSwitchToMagicLink}
                className="text-left underline text-slate-400 hover:text-slate-700 font-medium cursor-pointer"
              >
                Receive a login link via email instead
              </button>
            </div>
          </Form>
        )}
      </Formik>
    </>
  );
}
